<script setup>
import { ref, computed, onMounted } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import twentyApi from 'dashboard/api/integrations/twenty';
import CreateOrLinkOpportunity from './CreateOrLinkOpportunity.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const emit = defineEmits(['linked', 'unlinked']);

const TWENTY_LOGO = '/dashboard/images/integrations/twenty.png';

const STAGE_COLORS = {
  NEW: '#3B82F6',
  SCREENING: '#F59E0B',
  MEETING: '#8B5CF6',
  PROPOSAL: '#F97316',
  CUSTOMER: '#10B981',
  CHURNED: '#6B7280',
  LOST: '#EF4444',
};

// ─── state ───────────────────────────────────────────────────────────────────
const allLinked = ref([]);
const instanceUrl = ref('');
const isLoading = ref(false);
const shouldShowModal = ref(false);

const linkedList = computed(() =>
  allLinked.value.filter(r => r.twenty_record_type === 'opportunity')
);

const linkedPersonId = computed(() => {
  const person = allLinked.value.find(r => r.twenty_record_type === 'person');
  return person?.twenty_record_id || '';
});

// ─── fetch ───────────────────────────────────────────────────────────────────
const fetchLinked = async () => {
  isLoading.value = true;
  try {
    const { data } = await twentyApi.getLinkedRecords(props.conversationId);
    allLinked.value = data?.data || [];
    instanceUrl.value = data?.instance_url || '';
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchLinked);

// ─── helpers ─────────────────────────────────────────────────────────────────
const stageColor = stage => STAGE_COLORS[stage?.toUpperCase()] || '#6B7280';

// ─── open in Twenty ──────────────────────────────────────────────────────────
const openRecord = record => {
  if (!instanceUrl.value) return;
  window.open(
    `${instanceUrl.value}/object/opportunity/${record.twenty_record_id}`,
    '_blank'
  );
};

// ─── unlink ──────────────────────────────────────────────────────────────────
const unlink = async record => {
  await twentyApi.unlinkRecord(record.id);
  allLinked.value = allLinked.value.filter(r => r.id !== record.id);
  emit('unlinked');
};

// ─── on linked ───────────────────────────────────────────────────────────────
const onLinked = async () => {
  shouldShowModal.value = false;
  await fetchLinked();
  emit('linked');
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <p class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">
      {{ $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.TITLE') }}
    </p>

    <!-- Loading -->
    <div v-if="isLoading" class="text-xs text-n-slate-11">
      {{ $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.LOADING') }}
    </div>

    <template v-else>
      <!-- Add button: always at top -->
      <Button
        ghost
        xs
        icon="i-lucide-plus"
        :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.ADD_RECORD')"
        @click="shouldShowModal = true"
      />

      <!-- Linked opportunities list -->
      <div v-if="linkedList.length" class="flex flex-col gap-3">
        <div
          v-for="record in linkedList"
          :key="record.id"
          class="flex flex-col gap-3 px-3 py-2.5 rounded-xl border border-n-weak"
        >
          <!-- Header: badge (logo + name + open) + unlink -->
          <div class="flex items-start sm:items-center justify-between gap-2">
            <div
              class="flex items-center gap-2 px-2 py-1.5 border rounded-lg border-n-strong min-w-0 flex-1"
            >
              <div class="flex items-center gap-1 min-w-0 flex-1">
                <img :src="TWENTY_LOGO" class="size-4 shrink-0" alt="Twenty" />
                <span class="text-xs font-medium text-n-slate-12 truncate">
                  {{ record.twenty_record_name }}
                </span>
              </div>
              <span class="w-px h-3 bg-n-weak shrink-0" />
              <Button
                link
                xs
                slate
                icon="i-lucide-arrow-up-right"
                class="!size-4 shrink-0"
                @click="openRecord(record)"
              />
            </div>
            <Button
              ghost
              xs
              slate
              icon="i-lucide-unlink"
              class="shrink-0"
              @click="unlink(record)"
            />
          </div>

          <!-- Meta: company | stage (responsive wrap) -->
          <div
            v-if="
              record.twenty_record_data?.stage ||
              record.twenty_record_data?.company
            "
            class="flex flex-wrap items-center gap-x-1.5 gap-y-1"
          >
            <template v-if="record.twenty_record_data?.company">
              <Icon
                icon="i-lucide-building-2"
                class="size-3.5 text-n-slate-11 shrink-0"
              />
              <span class="text-xs text-n-slate-11 truncate max-w-[150px]">
                {{ record.twenty_record_data.company }}
              </span>
            </template>
            <template
              v-if="
                record.twenty_record_data?.company &&
                record.twenty_record_data?.stage
              "
            >
              <span class="w-px h-3 bg-n-weak shrink-0 hidden sm:block" />
            </template>
            <template v-if="record.twenty_record_data?.stage">
              <span
                class="size-2 rounded-full shrink-0"
                :style="{
                  backgroundColor: stageColor(record.twenty_record_data.stage),
                }"
              />
              <span class="text-xs text-n-slate-12 whitespace-nowrap">
                {{ record.twenty_record_data.stage }}
              </span>
            </template>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <p v-else class="text-xs text-n-slate-11 text-center py-2">
        {{ $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.OPPORTUNITY.EMPTY') }}
      </p>
    </template>

    <!-- Modal -->
    <woot-modal
      v-model:show="shouldShowModal"
      :on-close="() => (shouldShowModal = false)"
      :close-on-backdrop-click="false"
      class="!items-start [&>div]:!top-12 [&>div]:sticky"
    >
      <CreateOrLinkOpportunity
        v-if="shouldShowModal"
        :conversation-id="conversationId"
        :linked-person-id="linkedPersonId"
        :linked-opportunities="linkedList"
        @linked="onLinked"
        @close="shouldShowModal = false"
      />
    </woot-modal>
  </div>
</template>
